"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { IconMessageCircle, IconX } from "@tabler/icons-react";
import { ChatPanel } from "./chat-panel";
import { StartForm } from "./start-form";
import { OnlineIndicator } from "./online-indicator";
import { io, type Socket } from "socket.io-client";

const STORAGE_KEY = "coldfire_support_chat";

type Session = {
  ticketId: number;
  sessionToken: string;
  email?: string | null;
};

function readSession(): Session | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.ticketId === "number" &&
      typeof parsed.sessionToken === "string"
    ) {
      return parsed as Session;
    }
  } catch (_) {}
  return null;
}

function writeSession(session: Session | null) {
  if (typeof window === "undefined") return;
  if (!session) {
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }
}

function readResumeFromUrl(): { ticketId: number; sessionToken: string } | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const ticketId = Number(params.get("support_chat"));
  const sessionToken = params.get("token");
  if (!Number.isFinite(ticketId) || !sessionToken) return null;
  return { ticketId, sessionToken };
}

export function SupportChatWidget() {
  const t = useTranslations("support_chat");
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [agentsOnlineCount, setAgentsOnlineCount] = useState(0);
  const presenceSocketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const resume = readResumeFromUrl();
    if (resume) {
      const next: Session = { ticketId: resume.ticketId, sessionToken: resume.sessionToken };
      writeSession(next);
      setSession(next);
      setOpen(true);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("support_chat");
        url.searchParams.delete("token");
        window.history.replaceState({}, "", url.toString());
      } catch (_) {}
    } else {
      setSession(readSession());
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (session) return;
    const url = process.env.NEXT_PUBLIC_STRAPI_URL;
    if (!url) return;

    const socket = io(`${url.replace(/\/$/, "")}/support-chat`, {
      auth: {},
      transports: ["websocket", "polling"],
      reconnection: true,
    });

    presenceSocketRef.current = socket;

    socket.on("connect_error", () => {
      socket.disconnect();
    });
    socket.on("agents:online-count", (payload: { count: number }) => {
      setAgentsOnlineCount(payload?.count ?? 0);
    });

    return () => {
      socket.disconnect();
      presenceSocketRef.current = null;
    };
  }, [session]);

  const handleStarted = useCallback(
    (s: { ticketId: number; sessionToken: string; stage: string }) => {
      const next: Session = { ticketId: s.ticketId, sessionToken: s.sessionToken };
      writeSession(next);
      setSession(next);
    },
    []
  );

  const handleClose = useCallback(() => setOpen(false), []);

  const handleEnd = useCallback(() => {
    writeSession(null);
    setSession(null);
  }, []);

  if (!hydrated) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("widget_aria")}
        className="fixed bottom-5 right-5 z-50 inline-flex items-center justify-center h-14 w-14 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-xl transition"
      >
        {open ? (
          <IconX className="h-6 w-6" />
        ) : (
          <IconMessageCircle className="h-6 w-6" />
        )}
      </button>

      {open ? (
        <div className="fixed bottom-24 right-5 z-50 w-[360px] max-w-[calc(100vw-2.5rem)] h-[560px] max-h-[calc(100vh-7rem)] rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden bg-white dark:bg-neutral-950 flex flex-col">
          {session ? (
            <ChatPanel
              ticketId={session.ticketId}
              sessionToken={session.sessionToken}
              visitorEmail={session.email}
            />
          ) : (
            <div className="flex flex-col h-full">
              <header className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                    {t("header_title")}
                  </div>
                  <OnlineIndicator count={agentsOnlineCount} />
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  aria-label={t("close")}
                  className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
                >
                  <IconX className="h-4 w-4" />
                </button>
              </header>
              <div className="flex-1 overflow-y-auto">
                <StartForm
                  agentsOnline={agentsOnlineCount > 0}
                  onStarted={handleStarted}
                />
              </div>
            </div>
          )}

          {session ? (
            <button
              type="button"
              onClick={handleEnd}
              className="text-[11px] text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 px-3 py-1 border-t border-neutral-100 dark:border-neutral-800"
            >
              {t("end_session")}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
